import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "components/ui/input";
import { cn } from "lib/utils";

const KEYWORD_SEARCH_DEBOUNCE_MS = 600;

type DebouncedKeywordSearchFormProps = {
    currentKeyword: string;
    placeholder: string;
    onDebouncedChange: (keyword: string) => void;
    className?: string;
    iconClassName?: string;
    inputClassName?: string;
};

const DebouncedKeywordSearchForm: React.FC<DebouncedKeywordSearchFormProps> = ({
    currentKeyword,
    placeholder,
    onDebouncedChange,
    className,
    iconClassName,
    inputClassName,
}) => {
    const [keywordInput, setKeywordInput] = useState(currentKeyword);
    const [lastKeyword, setLastKeyword] = useState(currentKeyword);
    const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
    const lastSubmittedKeyword = useRef(currentKeyword);

    if (currentKeyword !== lastKeyword) {
        if (currentKeyword !== lastSubmittedKeyword.current) {
            setKeywordInput(currentKeyword);
        }
        setLastKeyword(currentKeyword);
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextKeyword = event.target.value;
        setKeywordInput(nextKeyword);
        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            const nextSubmittedKeyword = nextKeyword.trim();
            lastSubmittedKeyword.current = nextSubmittedKeyword;
            onDebouncedChange(nextSubmittedKeyword);
        }, KEYWORD_SEARCH_DEBOUNCE_MS);
    };

    useEffect(
        () => () => {
            clearTimeout(debounceTimer.current);
        },
        [],
    );

    return (
        <div className={cn("relative w-full", className)} role="search">
            <Search
                aria-hidden="true"
                className={cn(
                    "pointer-events-none absolute left-6 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400",
                    iconClassName,
                )}
            />
            <Input
                aria-label={placeholder}
                autoComplete="off"
                className={cn(
                    "h-16 w-full rounded-[20px] border-slate-100 bg-slate-50 py-5 pl-14 pr-6 text-base font-medium text-slate-700 placeholder:text-slate-400",
                    inputClassName,
                )}
                name="filter_keyword"
                onChange={handleChange}
                placeholder={placeholder}
                type="search"
                value={keywordInput}
            />
        </div>
    );
};

export default DebouncedKeywordSearchForm;
